import { Spinner, SpinnerSize } from "@fluentui/react";
import { PageRemoveIcon, TextDocumentIcon } from "@fluentui/react-icons-mdl2";
import { useState, useEffect } from "react";

import {
  InvoiceAccount,
  Invoice,
  Tariff
} from "../../interfaces/invoices-response";
import AccountInfo from "../accountCard/account-card";
import InvoiceCard from "../invoiceCard/invoice-card";
import SearchInput from "../invoiceInput/search-input";

import {
  AccountSection,
  Container,
  Content,
  EmptyInvoiceList,
  Header,
  InvoiceList,
  InvoicesSection,
  SectionTitle
} from "./style";

import { useApi, ExecuteData } from "@/core/libs/api/react-query/useApi";

interface SidebarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  account?: InvoiceAccount;
  invoices: Invoice[];
  expandedInvoices: number[];
  selectedTariff?: Tariff;
  onToggleInvoice: (invoiceId: number) => void;
  onTariffSelect: (tariff: Tariff) => void;
  isLoading?: boolean;
  document: string;
}

export default function Sidebar({
  searchValue,
  onSearchChange,
  onSearch,
  account,
  invoices,
  expandedInvoices,
  selectedTariff,
  onToggleInvoice,
  onTariffSelect,
  isLoading = false,
  document
}: SidebarProps) {
  const { execute } = useApi();
  const [invoiceTariffs, setInvoiceTariffs] = useState<
    Record<number, Tariff[]>
  >({});
  const [loadingTariffIds, setLoadingTariffIds] = useState<number[]>([]);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<number>(null);

  useEffect(() => {
    const tariffsFromInvoices: Record<number, Tariff[]> = {};

    invoices.forEach(invoice => {
      if (invoice.tariffs && invoice.tariffs.length > 0) {
        tariffsFromInvoices[invoice.id] = invoice.tariffs;
      }
    });

    setInvoiceTariffs(tariffsFromInvoices);
  }, [invoices]);

  const fetchInvoiceTariffs = async (invoiceId: number, document: string) => {
    setLoadingTariffIds(prev => [...prev, invoiceId]);
    setCurrentInvoiceId(invoiceId);

    try {
      const data: ExecuteData = {
        params: { document },
        url: `invoices/${invoiceId}/tariffs`,
        messages: {
          error: "Tivemos um problema ao carregar as tarifas da fatura"
        }
      };

      const response = await execute(data);

      if (response && response.data) {
        setInvoiceTariffs(prev => ({
          ...prev,
          [invoiceId]: response.data
        }));
      }
      setLoadingTariffIds(prev => prev.filter(id => id !== invoiceId));
    } catch (error) {
      throw new Error(error);
    }
  };

  const handleToggleInvoice = (invoiceId: number) => {
    const isExpanding = !expandedInvoices.includes(invoiceId);

    if (isExpanding) {
      if (!invoiceTariffs[invoiceId]) {
        fetchInvoiceTariffs(invoiceId, searchValue.replace(/\D/g, ""));
      }
      setCurrentInvoiceId(invoiceId);
    } else {
      setCurrentInvoiceId(null);
    }

    onToggleInvoice(invoiceId);
  };

  return (
    <Container>
      <Header>
        <SearchInput
          value={searchValue}
          onChange={onSearchChange}
          onSearch={onSearch}
          size="small"
        />
      </Header>

      <Content>
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "2rem"
            }}
          >
            <Spinner size={SpinnerSize.large} label="Carregando..." />
          </div>
        ) : (
          <>
            {account && (
              <AccountSection>
                <AccountInfo
                  contractChannel={account.contractChannel}
                  name={account.name}
                  document={account.document}
                  isAssociated={account.isAssociated}
                  contractNumber={account.contractNumber}
                />
              </AccountSection>
            )}

            <InvoicesSection>
              <SectionTitle>
                <TextDocumentIcon />
                Faturas
              </SectionTitle>

              {invoices.length > 0 ? (
                <InvoiceList>
                  {invoices.map(invoice => (
                    <InvoiceCard
                      key={invoice.id}
                      invoice={invoice}
                      expanded={expandedInvoices.includes(invoice.id)}
                      selectedTariff={selectedTariff}
                      onToggle={handleToggleInvoice}
                      onTariffSelect={onTariffSelect}
                      customTariffs={invoiceTariffs[invoice.id] || []}
                      isLoadingTariffs={loadingTariffIds.includes(invoice.id)}
                      currentInvoiceId={currentInvoiceId}
                      document={document}
                    />
                  ))}
                </InvoiceList>
              ) : (
                <EmptyInvoiceList>
                  <PageRemoveIcon
                    style={{
                      width: "30px",
                      height: "30px",
                      marginBottom: "10px"
                    }}
                  />
                  <p>Nenhuma fatura encontrada.</p>
                </EmptyInvoiceList>
              )}
            </InvoicesSection>
          </>
        )}
      </Content>
    </Container>
  );
}
